import React, { useEffect, useState } from 'react';
import './ProfilePage.css';
import Header from './Header';
import PhotoGrid from './PhotoGrid';
import ProfileCard from './ProfileCard';
import { contentData } from '../data/contentData';
import { ProfileCard as ProfileCardType } from '../types';
import { useAnalytics } from '../lib/analytics/client';

console.log('🔥 ProfilePage.tsx loaded - imports successful');

interface ProfilePageProps {
  onOpenCanvas: (card?: ProfileCardType) => void;
  onOpenCanvasWithText?: (text: string) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onOpenCanvas, onOpenCanvasWithText }) => {
  console.log('🔥 ProfilePage component rendering...');
  const analytics = useAnalytics();
  console.log('🔥 Analytics hook returned:', analytics);
  const [cards, setCards] = useState(contentData.cards);
  
  // Photos card metadata for the separate photos section
  const photosCard: ProfileCardType = {
    id: 'photos',
    type: 'photos' as const,
    title: 'Photos',
    metadata: {
      description: "Visual evidence that I'm a real person and not an AI catfish",
      personalContext: "These are my most socially acceptable photos - the weird ones are saved for date #3",
      storyBehind: "Each photo taken seconds before either disaster or triumph, no in-between",
      emotionalTone: "mysterious yet approachable",
      conversationStarters: [
        "Which photo captures my essence best?",
        "Can you guess which one my mom chose?",
        "Which version of me would you want to meet for coffee?"
      ],
      aiGuidance: "These photos show my range - from professional to 'just woke up but make it cute'",
      tags: ["not-photoshopped", "genuine-smile", "good-hair-days", "carefully-curated-casual"],
      importance: "high"
    }
  };

  // Track profile page view
  useEffect(() => {
    if (analytics) {
      analytics.trackProfileView();
    }
  }, [analytics]);

  // Enhanced canvas opening with analytics
  const handleCanvasOpen = (card?: ProfileCardType) => {
    if (analytics && card) {
      analytics.trackCanvasOpen(card.id, card.type, card.title);
    }
    onOpenCanvas(card);
  };

  const handleCanvasOpenWithText = (text: string) => {
    if (analytics) {
      analytics.trackCanvasOpen('search_bar', 'search', text);
    }
    if (onOpenCanvasWithText) {
      onOpenCanvasWithText(text);
    }
  };

  // Load metadata from backend on component mount
  useEffect(() => {
    const loadCardMetadata = async () => {
      try {
        const response = await fetch('/api/card-metadata');
        if (response.ok) {
          const cardsWithMetadata = await response.json();
          setCards(cardsWithMetadata);
        }
      } catch (error) {
        console.error('Error loading card metadata from backend:', error);
      }
    };
    
    loadCardMetadata();
  }, []);

  return (
    <div className="profile-page">
      <Header 
        name={contentData.profile.name}
        searchPlaceholder={contentData.profile.searchPlaceholder}
        onCreateClick={() => handleCanvasOpen()}
        onCreateWithText={handleCanvasOpenWithText}
      />
      
      {/* Analytics Debug Indicator */}
      {analytics && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0, 255, 0, 0.1)',
          border: '1px solid rgba(0, 255, 0, 0.3)',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          color: '#00ff00',
          fontFamily: 'monospace',
          zIndex: 9999
        }}>
          📊 Analytics Active<br/>
          Session: {analytics.getSessionId().substring(0, 15)}...
        </div>
      )}
      
      <div className="container">
        {/* Photos Section */}
        <div className="photos-section">
          <button 
            className="info-btn" 
            onClick={() => handleCanvasOpen(photosCard)}
          >
            click to chat
          </button>
          <PhotoGrid photos={contentData.photos} />
        </div>

        {/* Profile Cards */}
        <div className="masonry-grid">
          {cards.map((card) => (
            <ProfileCard 
              key={card.id} 
              card={card}
              onInfoClick={() => handleCanvasOpen(card)}
            />
          ))}
        </div>
        
        {/* Profile Reaction Section */}
        <div className="profile-reaction-divider"></div>
        <div className="profile-reaction-section">
          <div className="reaction-question">
            What did you think after going through the profile?
          </div>
          <ProfileReactionInput onSubmit={handleCanvasOpenWithText} />
        </div>
        
        
      </div>
    </div>
  );
};

// Profile Reaction Input Component
const ProfileReactionInput: React.FC<{ onSubmit: (text: string) => void }> = ({ onSubmit }) => {
  const [reaction, setReaction] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reaction.trim()) {
      onSubmit(reaction.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="reaction-input-form">
      <div className="reaction-input-container">
        <input
          type="text"
          value={reaction}
          onChange={(e) => setReaction(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="One word..."
          className="reaction-search-bar"
          maxLength={50}
        />
        <button type="submit" className="reaction-enter-btn">
          Enter
        </button>
      </div>
      <div className="reaction-hint">
        Chat with Anshika ✨
      </div>
    </form>
  );
};

export default ProfilePage;